import "./App.css";

import { 
  day1Date, 
  todayDate, 
  maxGuesses, 
  dateToNumber, 
  day1Number, 
  todayDayNum, 
  dayNum, 
  allowPractice, 
  practice, 
  urlParam, 
  gameName, 
  isDev,  
  cheat } from "./util";

import Game, { emojiBlock, GameState } from "./Game";
import { useEffect, useState } from "react";
import { About } from "./About";
import { GetDay, Stats } from "./Stats";
import Calendar from "react-calendar";
import cheatyface from "./cheatyface.json"
import { StatusView } from "./StatusView";

const status = urlParam("status");

function encode( str:string ) {
  return window.btoa(str);
}

function decode( str:string ) {
  return window.atob(str);
}

export function serializeStorage() : string {
  const strPlain = window.JSON.stringify(window.localStorage);
  const strEncoded = encode(strPlain);
  return strEncoded;
}

export function deserializeStorage(serialized: string) {
  try {
    let o = window.JSON.parse(decode(serialized));
    for (let [key, value] of Object.entries(o)) {
      if ( window.localStorage.getItem(key) === null ) {
        window.localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value))
      }
    }
  } catch(e) {
    console.log(e);
  }
}

function useSetting<T>(
  key: string,
  initial: T
): [T, (value: T | ((t: T) => T)) => void] {
  const [current, setCurrent] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initial;
    } catch (e) {
      return initial;
    }
  });
  const setSetting = (value: T | ((t: T) => T)) => {
    try {
      const v = value instanceof Function ? value(current) : value;
      setCurrent(v);
      window.localStorage.setItem(key, JSON.stringify(v));
    } catch (e) {}
  };
  return [current, setSetting];
}

async function share(text?: string) {
  const body = (text ? text + "\n" : "");
  if (
    /android|iphone|ipad|ipod|webos/i.test(navigator.userAgent) &&
    !/firefox/i.test(navigator.userAgent)
  ) {
    try {
      await navigator.share({ text: body });
      return;
    } catch (e) {
      console.warn("navigator.share failed:", e);
    }
  }
  try {
    await navigator.clipboard.writeText(body);
    return;
  } catch (e) {
    console.warn("navigator.clipboard.writeText failed:", e);
  }
}

const redirectFrom = [""];
export const redirectTo = "https://double-dabble.com/";
const importResponse = new URLSearchParams(window.location.search).get("importResponse") ?? "";
const importPayload = importResponse !== "" ? window.location.hash.slice(1) : "";
const importRequest = new URLSearchParams(window.location.search).get("importRequest") ?? "";
const isHttp = window.location.protocol === "http:";

export function readOnly() {
  for(var from of [...redirectFrom]) {
    if (window.location.host.lastIndexOf(from) === 0) {
      return true;
    }
  }
  return false;
}

function App() {
  type Page = "game" | "about" | "settings" | "stats" | "calendar";
  const [page, setPage] = useState<Page>("game");
  const prefersDark = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
  const prefersLight = window.matchMedia && window.matchMedia("(prefers-color-scheme: light)").matches;
  const [dark, setDark] = useSetting<boolean>("dark-mode-theme", !window.matchMedia || prefersDark || !prefersLight);
  const [hardMode, setHardMode] = useSetting<boolean>("hardMode", false);
  const [colorBlind, setColorBlind] = useSetting<boolean>("colorblind", false);
  const [keyboard, setKeyboard] = useSetting<string>(
    "keyboard",
    "xxxxx-xxxxx-BxxxE-DL_RS"
  );
  const [enterLeft, setEnterLeft] = useSetting<boolean>("enter-left", false);
  const [liveReplays, setLiveReplays] = useSetting<boolean>("live-replays", true);

  useEffect(() => { 
    
    if (Number(dayNum) > Number(todayDayNum) && !isDev) {
      window.location.replace(redirectTo);
      return;
    }    

    if (importPayload !== "") {
      if (window.localStorage.getItem("waiting-import") !== null) {
        window.localStorage.removeItem("waiting-import");
        deserializeStorage(importPayload);
      }
      return;
    }

    for(var from of redirectFrom) {
      if (window.location.host.lastIndexOf(from) === 0) {
        if (importRequest !== "") {
          const str = serializeStorage();
          window.location.replace(redirectTo + "?importResponse=1#" + str);
        }
        if ( urlParam("preventRedirect") === null ) {
          window.location.replace(redirectTo);
        }
        return;
      }  
    }
  });

  useEffect(() => {
    document.body.className = dark ? "dark" : "";
    setTimeout(() => {
      // Avoid transition on page load
      document.body.style.transition = "0.3s background-color ease-out";
    }, 1);
  }, [dark]);

  const link = (emoji: string, label: string, page: Page) => (
    <button
      className={emoji === "" ? ("link-Image link-"+label) : "emoji-link"}
      onClick={() => { setPage(page); if ( page !== "game") { setMenuExpanded(false); } }}
      title={label}
      aria-label={label}
    >
      {emoji}
    </button>
  );

  function calendarTileContent(activeStartDate: any, date: Date, view: any) {
    let day = GetDay(date);
    return ( day && <pre>{emojiBlock(day, colorBlind)}</pre> );
  }

  function calendarFormatDay(locale: string, date: Date) {
    let day = GetDay(date);
    let hardModeIndicator = "";
    let result = "";
    if ( day ) {
      result = day.gameState === GameState.Playing
      ? "🎲"
      : day.gameState === GameState.Won
      ? "🟢"
      : "💀";
      hardModeIndicator = day.hardMode ? "" : "";
    }
    return date.toLocaleDateString(locale, { day: "numeric" }) + hardModeIndicator +  result;
  }

 function monthEmojiBlock(date: Date) : string {
    let result = "";
    for(let i = 0; i < new Date(date.getFullYear(), date.getMonth(), 1).getDay(); ++i)
    {
      result += "⬛";
    }
    let numDays = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    for(let dayNumber = 1; dayNumber <= numDays; ++dayNumber)
    {
      let thisDate = new Date(date.getFullYear(), date.getMonth(), dayNumber);
      let day = GetDay(thisDate);
      if ( day ) {
        result += day.gameState === GameState.Playing
          ? "🎲"
          : day.gameState === GameState.Won
          ? "🟢"
          : "💀";
      } else {
        result += "⬛";
      }
      if(thisDate.getDay() == 6) {
        result += "\n";
      }
    }
    return result.trimEnd();
  }  

  const [startDate, setStartDate] = useState<Date>(todayDate);
  const [menuExpanded, setMenuExpanded] = useState<boolean>(false);

  if (status != null) {
    return <StatusView status={status} />;
  }
  
  const dailyLink = "/";
  const practiceLink = "/?unlimited";

  if (Number(dayNum) > Number(todayDayNum) && !isDev) {
    return (
      <div className={"App-container" + (colorBlind ? " color-blind" : "")}>
      <h1>
        <div className="Game-name-mode-container">
          <span className="Game-name">
          <div className="stack stacks3">
            <span className="index0"></span>
            <span className="index1">{gameName}</span>
            <span className="index2">{gameName}</span>
          </div>
          </span>             
        </div>
        <div className="Game-modes">
          Rewinding Time...
        </div>
      </h1>
      </div>
    )
  }
  
  return (
    <div className={"App-container" + (colorBlind ? " color-blind" : "")}>
      <h1>
        <div className="Game-name-mode-container">
        <span className="Game-name">
         <img width="23" height="23" src="favicon.png"/> {gameName}          
         </span>             
        <div className="Game-modes">
        {!readOnly() && allowPractice && !practice && <a className="ModeEnabled">Daily</a>}
        {!readOnly() && allowPractice && practice && <a className="ModeDisabled" href={dailyLink}>Daily</a>}
        {!readOnly() && allowPractice && practice && <a className="ModeEnabled">Unlimited</a>}
        {!readOnly() && allowPractice && !practice && <a className="ModeDisabled" href={practiceLink}>Unlimited</a>}
        </div>
        </div>
      </h1>
      { 
      <div className="top-right">
        <button className={"collapsible link-Image" + ((menuExpanded || page !== "game") ? "" : " active")} onClick={() => 
        {
          if(menuExpanded || page !== "game") {
            setPage("game");
            setMenuExpanded(false);
          } else {
            setMenuExpanded(true);
          }
        }
        }></button>
      </div>            
      }
      {<div className={"top-right content " + (menuExpanded ? "menuExpanded" : "menuCollapsed")}>
        {page !== "game" ? ( 
          <div/>        
        ) : (
          <div className="flowDown">
            {!readOnly() && link("", "About", "about")}
            {!readOnly() && link("", "Settings", "settings")}            
            {link("", "Stats", "stats")}
            {link("", "Calendar", "calendar")}
          </div>
        )}
      </div>
      }
      <div
        style={{
          position: "absolute",
          left: 5,
          top: 5,
          visibility: page === "game" ? "visible" : "hidden",
        }}
      >
      </div>
      {page === "about"  && <About />}
      {page === "stats" && <Stats />}
      {page === "calendar" && <Calendar 
        maxDate={isDev ? new Date("January 1, 3000") : todayDate}
        minDate={day1Date}
        minDetail={"month"}
        maxDetail={"month"}
        onClickDay={(value: Date, event: any) => {
          if ( isDev ) {
            window.location.replace(window.location.origin + "?x="+(1 + dateToNumber(value) - day1Number) + "&xyzzyx=" + cheatyface["password"]);
          } else if ( value >= day1Date && value <= todayDate)  {
            window.location.replace(window.location.origin + "?x="+(1 + dateToNumber(value) - day1Number) + (urlParam("preventRedirect") !== null ? "&preventRedirect" : ""));
          }
        }}
        formatDay={(locale: string, date: Date) => calendarFormatDay(locale, date)}
        tileContent={({ activeStartDate, date, view }) => calendarTileContent(activeStartDate, date, view) }
        onActiveStartDateChange={ ({ action, activeStartDate, value, view }) => {setStartDate(activeStartDate);}}
      />}
      {page === "calendar" && (
        <div>
        <button
          onClick={() => {share( 
            `${gameName} ` + startDate.toLocaleString('default', { month: 'long', year: 'numeric' }) + '\n' + monthEmojiBlock(startDate));}}
        >
          Share month results
        </button>
        {isHttp && <div className="Game-stats-notice">Not seeing what you expect? Note that history is separate between secure (https) and non-secure sites (http). Maybe you meant to be on <a href="https://double-dabble.com">https://double-dabble.com</a></div>}
        </div> )
      }

      {page === "settings" && (
        <div className="Settings">
          <div className="Settings-setting">
            <input
              id="dark-setting"
              type="checkbox"
              checked={dark}
              onChange={() => setDark((x: boolean) => !x)}
            />
            <label htmlFor="dark-setting">Dark theme</label>
          </div>
          <div className="Settings-setting">
            <input
              id="colorblind-setting"
              type="checkbox"
              checked={colorBlind}
              onChange={() => setColorBlind((x: boolean) => !x)}
            />
            <label htmlFor="colorblind-setting">High-contrast colors</label>
          </div>
          <div className="Settings-setting">
            <input
              id="enter-left-setting"
              type="checkbox"
              checked={enterLeft}
              onChange={() => setEnterLeft((x: boolean) => !x)}
            />
            <label htmlFor="enter-left-setting">"Enter" on left side</label>
          </div>
          <div className="Settings-setting">
            <input
              id="live-replays-setting"
              type="checkbox"
              checked={liveReplays}
              onChange={() => setLiveReplays((x: boolean) => !x)}
            />
            <label htmlFor="live-replays-setting">Live / Replays — When enabled, you can watch other players's solutions live or as a replay after solving a puzzle yourself, and likewise your solution goes into the pool for others to watch live or as a replay.</label>
          </div>
        </div>
      )}
      {<Game
        maxGuesses={maxGuesses}
        hidden={page !== "game"}
        colorBlind={colorBlind}
        hardMode={practice ? urlParam("hard") !== null : hardMode}
        keyboardLayout={keyboard.replaceAll(
          /[BE]/g,
          (x) => (enterLeft ? "EB" : "BE")["BE".indexOf(x)]
        )}
        liveReplays={liveReplays}
        setLiveReplays={setLiveReplays}
      />
      }
      <div style={{ 
        width: '100%',
        marginTop: 'auto',
        paddingTop: '20px'
      }}>
       <iframe 
          src="https://kellydornhaus.com/embed.html?from=double-dabble" 
          style={{width: '100%', height: '139px', border: 'none', overflow: 'hidden'}}
          scrolling="no"
          allow="autoplay"
        />
      </div>      
    </div>
  );
}

export default App;
